import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import ElectronicPrescriptionsHeader from "./Header/ElectronicPrescriptionsHeader";
import { MaterialReactTable } from "material-react-table";
import SaveElectronicPrescriptionPopup from "./Popup/SaveElectronicPrescriptionPopup";
import ViewElectronicPrescriptionPopup from "./Popup/ViewElectronicPrescriptionPopup";

const ElectronicPrescriptionsListBody = () => {
  const { t } = useTranslation();
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [rowSelection, setRowSelection] = useState({});
  const [selectedPrescriptionIds, setSelectedPrescriptionIds] = useState([]);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Static data for electronic prescriptions
  const [electronicPrescriptions, setElectronicPrescriptions] = useState([
    {
      id: 1,
      patientName: "John Doe",
      medication: "Paracetamol",
      dosage: "500mg",
      instructions: "Take twice daily after meals",
      prescriptionDate: "2025-11-20",
      status: "Active",
    },
    {
      id: 2,
      patientName: "Jane Smith",
      medication: "Ibuprofen",
      dosage: "200mg",
      instructions: "Take every 6 hours as needed",
      prescriptionDate: "2025-11-18",
      status: "Active",
    },
    {
      id: 3,
      patientName: "Bob Johnson",
      medication: "Amoxicillin",
      dosage: "500mg",
      instructions: "Take three times daily for 7 days",
      prescriptionDate: "2025-11-15",
      status: "Completed",
    },
  ]);

  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null);

  const addOrUpdatePrescription = (prescription) => {
    if (selectedPrescriptionId) {
      // Update
      setElectronicPrescriptions((prev) =>
        prev.map((p) =>
          p.id === selectedPrescriptionId ? { ...p, ...prescription } : p
        )
      );
    } else {
      // Add
      const newId = Math.max(...electronicPrescriptions.map((p) => p.id)) + 1;
      setElectronicPrescriptions((prev) => [
        ...prev,
        { id: newId, ...prescription },
      ]);
    }
  };

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  // Table columns
  const columns = [
    {
      accessorKey: "#",
      header: t("Actions"),
      Cell: ({ row }) => (
        <button
          className="action_btn_mui"
          data-bs-toggle="offcanvas"
          data-bs-target="#saveElectronicPrescriptionOffCanvas"
          aria-controls="saveElectronicPrescriptionOffCanvas"
        >
          <span className="d-block material-symbols-outlined horz_icon">
            more_horiz
          </span>
        </button>
      ),
      enableColumnActions: false, // Hides the column action icon
      enableColumnDragging: false, // Hides the move icon
      enableSorting: false,
    },
    {
      accessorKey: "patientName",
      header: t("Patient Name"),
      size: 150,
    },
    {
      accessorKey: "medication",
      header: t("Medication"),
      size: 150,
    },
    {
      accessorKey: "dosage",
      header: t("Dosage"),
      size: 100,
    },
    {
      accessorKey: "instructions",
      header: t("Instructions"),
      size: 200,
    },
    {
      accessorKey: "prescriptionDate",
      header: t("Date"),
      size: 120,
    },
    {
      accessorKey: "status",
      header: t("Status"),
      size: 100,
      Cell: ({ cell }) => (
        <span
          className={`badge ${
            cell.getValue() === "Active" ? "bg-success" : "bg-secondary"
          }`}
        >
          {cell.getValue()}
        </span>
      ),
    },
  ];

  const updateBulkHandler = () => {
    // For now, no bulk action
  };

  const reloadList = () => {
    // For static data, no reload needed
  };

  const selectedItemIds = []; // For now, empty

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    (moduleAccess.includes("MOD_PATIENT_MANAGEMENT") &&
      moduleAccess.includes("MOD_ELECTRONIC_PRESCRIPTION_MANAGEMENT"))
  ) {
    return (
      <>
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Electronic Prescriptions")}
          />
          <div className="container-fluid px-lg-5">
            <ElectronicPrescriptionsHeader
              reloadList={reloadList}
              selectedItemIds={selectedItemIds}
            />
            <div className="mt-4">
              <MaterialReactTable
                columns={columns}
                data={electronicPrescriptions}
                enableRowSelection
                enableColumnOrdering
                enableGlobalFilter={false}
                enableColumnFilters={false}
                enablePagination
                enableSorting
                state={{ rowSelection, columnOrder }}
                initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                defaultColumn={{
                  minSize: 20,
                  maxSize: 300,
                  size: 50, //make columns wider by default
                }}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: "60vh",
                  },
                }}
              />
            </div>
          </div>

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
        <SaveElectronicPrescriptionPopup
          selectedPrescriptionId={selectedPrescriptionId}
          setSelectedPrescriptionId={setSelectedPrescriptionId}
          afterActionClose={() => {}}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setMessageType={setMessageType}
          addOrUpdatePrescription={addOrUpdatePrescription}
          selectedPrescription={electronicPrescriptions.find(
            (p) => p.id === selectedPrescriptionId
          )}
        />
        <ViewElectronicPrescriptionPopup
          selectedPrescription={electronicPrescriptions.find(
            (p) => p.id === selectedPrescriptionId
          )}
        />
      </>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default ElectronicPrescriptionsListBody;
